/**
 * Slideout Component
 * 
 * A reusable slideout wrapper that extends Untitled UI SlideoutMenu with app-specific defaults.
 * Provides title, close button, and pre-built button helpers with internationalization.
 * 
 * @example
 * // Simple usage with direct children
 * <Slideout title="Settings">
 *   <div className="space-y-4">
 *     <input placeholder="Name" />
 *     <input placeholder="Email" />
 *   </div>
 * </Slideout>
 * 
 * @example
 * // With pre-built buttons
 * <Slideout 
 *   title="Delete Item" 
 *   subtitle="This action cannot be undone"
 *   buttons={[
 *     <Slideout.CancelButton onCancel={close} />,
 *     <Slideout.DeleteButton onDelete={() => { deleteItem(); close(); }} />
 *   ]}
 * >
 *   {t('general.confirmDelete')}
 * </Slideout>
 * 
 * @example
 * // Advanced usage with render props for callbacks
 * <Slideout title="User Profile">
 *   {({ close, isEntering, isExiting }) => (
 *     <form onSubmit={(e) => { handleSave(e); close(); }}>
 *       <input name="name" />
 *       <div className="mt-4 flex gap-2">
 *         <button type="button" onClick={close}>Cancel</button>
 *         <button type="submit" disabled={isExiting}>
 *           {isExiting ? 'Saving...' : 'Save'}
 *         </button>
 *       </div>
 *     </form>
 *   )}
 * </Slideout>
 * 
 * @example
 * // Usage with Link component
 * <Link slideout={
 *   <Slideout title="Confirmation" hideCloseButton>
 *     {({ close }) => (
 *       <div>
 *         <p>Continue with this action?</p>
 *         <div className="flex gap-2 mt-4">
 *           <Slideout.CancelButton onCancel={close} />
 *           <Slideout.SaveButton onSave={() => { doAction(); close(); }}>
 *             Continue
 *           </Slideout.SaveButton>
 *         </div>
 *       </div>
 *     )}
 *   </Slideout>
 * }>
 *   Open Slideout
 * </Link>
 * 
 * @props
 * - title: string - Slideout header title (required)
 * - subtitle?: string - Optional subtitle below title
 * - children: ReactNode | (props) => ReactNode - Slideout content or render function
 * - buttons?: ReactElement[] - Array of button elements for footer
 * - hideCloseButton?: boolean - Hide the X close button (default: false)
 * 
 * @button-helpers
 * - Slideout.SaveButton - Primary button with "Save" text (t('general.save'))
 * - Slideout.CancelButton - Secondary button with "Cancel" text (t('general.cancel'))  
 * - Slideout.DeleteButton - Destructive button with "Delete" text (t('general.delete'))
 * 
 * @render-props (when using function children)
 * - close: () => void - Function to close the slideout
 * - isEntering: boolean - True when slideout is animating in
 * - isExiting: boolean - True when slideout is animating out
 */

import type { ReactElement, ReactNode } from 'react';
import * as React from 'react';
import { SlideoutMenu } from '@/components/_untitled/application/slideout-menus/slideout-menu';
import { SaveButton, CancelButton, DeleteButton } from '@/components/ui/ButtonHelpers';

interface SlideoutProps {
  title: string;
  subtitle?: string;
  children: ReactNode | ((props: { close: () => void; isEntering: boolean; isExiting: boolean }) => ReactNode);
  buttons?: ReactElement[];
  hideCloseButton?: boolean;
  onClose?: () => void;
}

const SlideoutComponent = ({ title, subtitle, children, buttons, hideCloseButton = false, onClose }: SlideoutProps) => {
  return (
    <>
      <SlideoutMenu.Header onClose={!hideCloseButton ? onClose : undefined}>
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        {subtitle && <p className="text-sm text-gray-600 mt-1">{subtitle}</p>}
      </SlideoutMenu.Header>
      <SlideoutMenu.Content>
        {typeof children === 'function' 
          ? children({ close: onClose || (() => {}), isEntering: false, isExiting: false })
          : children
        }
      </SlideoutMenu.Content>
      {buttons && (
        <SlideoutMenu.Footer>
          <div className="flex gap-3 border-t border-gray-200 pt-6">
            {buttons.map((button, index) => (
              <div key={index} className="flex-1">
                {React.cloneElement(button, {
                  onClose: onClose,
                  ...button.props
                })}
              </div>
            ))}
          </div>
        </SlideoutMenu.Footer>
      )}
    </>
  );
};

// Export Slideout with button helpers attached
export const Slideout = Object.assign(SlideoutComponent, {
  SaveButton,
  CancelButton,
  DeleteButton,
});